import React, { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Search,
  FolderTree,
  Download,
  ChevronLeft,
  ChevronRight,
  Folder,
  FolderOpen,
  Layers,
  Edit2,
  Trash2,
  RefreshCw,
} from "lucide-react";
import apiService from "../services/api";
import Button from "../components/ui/Button";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

// Helper to build tree from flat list
function buildTree(categories) {
  const map = {};
  const roots = [];

  // First pass: create the map
  categories.forEach((cat) => (map[cat.id] = { ...cat, children: [] }));

  // Second pass: build the tree structure
  categories.forEach((cat) => {
    if (cat.parentId) {
      // Check if parent exists in the map
      if (map[cat.parentId]) {
        map[cat.parentId].children.push(map[cat.id]);
      } else {
        // Parent doesn't exist, treat as root (orphaned category)
        console.warn(
          `Category "${cat.name}" (ID: ${cat.id}) has missing parent (ID: ${cat.parentId}). Treating as root.`
        );
        roots.push(map[cat.id]);
      }
    } else {
      roots.push(map[cat.id]);
    }
  });

  return roots;
}

// Tree Node Component
const TreeNode = ({
  node,
  level = 0,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  onAdd,
  editingId,
  newName,
  newCode,
  setNewName,
  setNewCode,
  onEditSave,
  addingFor,
  onAddSave,
  onAddCancel,
  categories,
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expanded[node.id];
  const isEditing = editingId === node.id;
  const isAdding = addingFor === node.id;

  const getCategoryPath = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    if (!category) return "";

    const path = [category.name];
    let curr = category;

    while (curr.parentId) {
      const parent = categories.find((cat) => cat.id === curr.parentId);
      if (parent) {
        path.unshift(parent.name);
        curr = parent;
      } else {
        break;
      }
    }

    return path.join(" > ");
  };

  return (
    <div className="tree-node">
      <div
        className="flex items-center space-x-3 py-2.5 px-3 rounded-lg transition-all duration-200 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 group relative"
        style={{ marginLeft: `${level * 32}px` }}
      >
        {/* Vertical connecting line for child nodes */}
        {level > 0 && (
          <div className="absolute left-[-16px] top-0 bottom-0 w-px bg-gradient-to-b from-gray-300 to-gray-200"></div>
        )}

        {/* Horizontal connecting line */}
        {level > 0 && (
          <div className="absolute left-[-16px] top-1/2 w-4 h-px bg-gray-300"></div>
        )}

        {/* Expand/Collapse Button */}
        {hasChildren ? (
          <button
            className="w-7 h-7 flex items-center justify-center text-blue-600 hover:bg-blue-100 rounded-lg transition-all duration-200 flex-shrink-0 border border-blue-200"
            onClick={() => onToggle(node.id)}
          >
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${
                isExpanded ? "rotate-90" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        ) : (
          <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
          </div>
        )}

        {/* Category Icon with level-based color */}
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-md flex-shrink-0 ${
            level === 0
              ? "bg-gradient-to-br from-blue-600 to-blue-700"
              : level === 1
              ? "bg-gradient-to-br from-indigo-500 to-indigo-600"
              : level === 2
              ? "bg-gradient-to-br from-purple-500 to-purple-600"
              : "bg-gradient-to-br from-pink-500 to-pink-600"
          }`}
        >
          {node.name.charAt(0).toUpperCase()}
        </div>

        {/* Category Content */}
        {isEditing ? (
          <div className="flex-1 flex items-center space-x-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="flex-1 px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Category name"
              autoFocus
            />
            <input
              type="text"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              className="w-24 px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Code"
            />
            <button
              onClick={onEditSave}
              className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
            >
              Save
            </button>
            <button
              onClick={() => onEdit(null)}
              className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 flex-wrap">
                <span className="font-semibold text-gray-900 text-base">
                  {node.name}
                </span>
                <span className="text-xs bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-2.5 py-1 rounded-full font-mono font-medium shadow-sm">
                  {node.code}
                </span>
                {level > 0 && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded italic">
                    Level {level + 1}
                  </span>
                )}
                {hasChildren && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">
                    {node.children.length} sub
                    {node.children.length === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              {level > 0 && (
                <div className="text-xs text-gray-500 mt-1 flex items-center space-x-1">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                    />
                  </svg>
                  <span className="truncate">
                    {getCategoryPath(node.parentId)}
                  </span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <button
                onClick={() => onAdd(node.id)}
                className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-all hover:scale-110"
                title="Add subcategory"
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                onClick={() => onEdit(node.id)}
                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-all hover:scale-110"
                title="Edit category"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
              <button
                onClick={() => onDelete(node.id)}
                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-all hover:scale-110"
                title="Delete category"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Add New Subcategory Form */}
      {isAdding && (
        <div
          className="ml-12 mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200"
          style={{ marginLeft: `${(level + 1) * 24}px` }}
        >
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="flex-1 px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="New subcategory name"
              autoFocus
            />
            <input
              type="text"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              className="w-32 px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              placeholder="Auto: C####"
              title="Auto-generated. You can customize if needed."
            />
            <button
              onClick={onAddSave}
              className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
            >
              Add
            </button>
            <button
              onClick={onAddCancel}
              className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Render Children */}
      {hasChildren && isExpanded && (
        <div className="mt-1">
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              expanded={expanded}
              onToggle={onToggle}
              onEdit={onEdit}
              onDelete={onDelete}
              onAdd={onAdd}
              editingId={editingId}
              newName={newName}
              newCode={newCode}
              setNewName={setNewName}
              setNewCode={setNewCode}
              onEditSave={onEditSave}
              addingFor={addingFor}
              onAddSave={onAddSave}
              onAddCancel={onAddCancel}
              categories={categories}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [addingFor, setAddingFor] = useState(null);
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [showAddRootModal, setShowAddRootModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({
    show: false,
    id: null,
    name: "",
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalParentId, setAddModalParentId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Calculate stats
  const stats = useMemo(() => {
    const totalCategories = categories.length;
    const rootCategories = categories.filter((c) => !c.parentId).length;
    const withChildren = categories.filter((c) =>
      categories.some((child) => child.parentId === c.id)
    ).length;
    const leafCategories = totalCategories - withChildren;

    return {
      totalCategories,
      rootCategories,
      withChildren,
      leafCategories,
    };
  }, [categories]);

  useEffect(() => {
    fetchCategories();
  }, []);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await apiService.getCategories();
      console.log("Fetched categories:", data);
      setCategories(data);
      setError(null);

      // Auto-expand all categories by default
      const expandedState = {};
      data.forEach((cat) => (expandedState[cat.id] = true));
      setExpanded(expandedState);
    } catch (err) {
      console.error("Fetch categories error:", err);
      setError(err.message);
      toast.error("Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };

  const generateNextCode = async () => {
    try {
      const response = await apiService.getNextCategoryCode();
      return response.nextCode || "C0001";
    } catch (err) {
      console.error("Error generating next code:", err);
      // Fallback to local generation if API fails
      if (categories.length === 0) {
        return "C0001";
      }

      const codes = categories
        .map((cat) => cat.code)
        .filter((code) => code && code.startsWith("C"))
        .map((code) => {
          const match = code.match(/C(\d+)/);
          return match ? parseInt(match[1], 10) : 0;
        })
        .filter((num) => !isNaN(num));

      const maxCode = codes.length > 0 ? Math.max(...codes) : 0;
      const nextNumber = maxCode + 1;

      return `C${nextNumber.toString().padStart(4, "0")}`;
    }
  };

  const handleToggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleEdit = (id) => {
    if (id) {
      const category = categories.find((cat) => cat.id === id);
      setNewName(category.name);
      setNewCode(category.code);
    }
    setEditingId(id);
  };

  const handleEditSave = async () => {
    if (!newName.trim() || !newCode.trim()) {
      toast.error("Name and code are required");
      return;
    }

    try {
      await apiService.updateCategory(editingId, {
        name: newName,
        code: newCode,
      });
      toast.success("Category updated successfully");
      setEditingId(null);
      setNewName("");
      setNewCode("");
      fetchCategories();
    } catch (err) {
      toast.error(err.message || "Failed to update category");
    }
  };

  const handleDelete = async (id) => {
    const category = categories.find((cat) => cat.id === id);
    setDeleteConfirm({ show: true, id, name: category?.name || "" });
  };

  const confirmDelete = async () => {
    try {
      await apiService.deleteCategory(deleteConfirm.id);
      toast.success("Category deleted successfully");
      setDeleteConfirm({ show: false, id: null, name: "" });
      fetchCategories();
    } catch (err) {
      toast.error(err.message || "Failed to delete category");
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, id: null, name: "" });
  };

  const handleAdd = async (parentId) => {
    setAddingFor(parentId);
    setNewName("");
    const nextCode = await generateNextCode();
    setNewCode(nextCode);
  };

  const handleAddSave = async () => {
    if (!newName.trim()) {
      toast.error("Category name is required");
      return;
    }

    const code = newCode.trim() || (await generateNextCode());

    try {
      await apiService.createCategory({
        name: newName,
        code: code,
        parentId: addingFor,
      });
      toast.success("Category created successfully");
      setAddingFor(null);
      setNewName("");
      setNewCode("");
      fetchCategories();
    } catch (err) {
      toast.error(err.message || "Failed to create category");
    }
  };

  const handleAddCancel = () => {
    setAddingFor(null);
    setNewName("");
    setNewCode("");
  };

  const handleAddRoot = async () => {
    if (!newName.trim()) {
      toast.error("Category name is required");
      return;
    }

    try {
      const code = newCode.trim() || (await generateNextCode());
      await apiService.createCategory({
        name: newName.trim(),
        code: code,
        parentId: null,
      });
      toast.success("Category created successfully");

      setShowAddRootModal(false);
      setNewName("");
      setNewCode("");
      fetchCategories();
    } catch (err) {
      toast.error(err.message || "Failed to create category");
    }
  };

  const handleExport = () => {
    const exportData = categories.map((cat) => ({
      ID: cat.id,
      Name: cat.name,
      Code: cat.code,
      "Parent ID": cat.parentId || "",
      Path: cat.path || cat.name,
      "Created At": new Date(cat.createdAt).toLocaleString(),
      "Updated At": new Date(cat.updatedAt).toLocaleString(),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Categories");
    XLSX.writeFile(
      wb,
      `categories_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    toast.success("Categories exported successfully");
  };

  const tree = buildTree(categories);
  console.log("Built tree:", tree);
  console.log("Categories count:", categories.length);

  // Filter categories based on search term
  const filteredCategories = searchTerm
    ? categories.filter(
        (cat) =>
          cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cat.code.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : categories;

  const filteredTree = searchTerm
    ? tree.filter(
        (node) =>
          node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          node.code.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : tree;

  // Build category paths and sort for display
  const sortedCategories = filteredCategories
    .map((category) => {
      const buildCategoryPath = (cat) => {
        const path = [cat.name];
        let current = cat;
        while (current.parentId) {
          const parentCat = categories.find((c) => c.id === current.parentId);
          if (parentCat) {
            path.unshift(parentCat.name);
            current = parentCat;
          } else {
            break;
          }
        }
        return path.join(" / ");
      };
      return {
        ...category,
        fullPath: buildCategoryPath(category),
      };
    })
    .sort((a, b) => a.fullPath.localeCompare(b.fullPath));

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalItems = sortedCategories.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedCategories = sortedCategories.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 dark:bg-gray-900 py-6 px-4 sm:px-6 lg:px-8">
      {/* Modern Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Product Categories
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage your product category hierarchy
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchCategories}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
          >
            <Download size={16} />
            Export
          </button>
          <button
            onClick={async () => {
              setNewName("");
              const nextCode = await generateNextCode();
              setNewCode(nextCode);
              setShowAddRootModal(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-xl shadow-sm transition-all"
          >
            <Plus size={16} />
            Add Category
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <Layers className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Total Categories
              </p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {stats.totalCategories}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-green-50 dark:bg-green-900/30 rounded-lg">
              <FolderTree className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Root Categories
              </p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {stats.rootCategories}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
              <FolderOpen className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Parent Categories
              </p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {stats.withChildren}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
              <Folder className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Leaf Categories
              </p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {stats.leafCategories}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6 shadow-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search categories by name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-colors"
          />
        </div>
        {searchTerm && (
          <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Found {totalItems} {totalItems === 1 ? "category" : "categories"}{" "}
            matching "{searchTerm}"
          </div>
        )}
      </div>

      {/* Categories Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Full Path
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Has Children
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedCategories.map((category) => {
                const hasChildren = categories.some(
                  (c) => c.parentId === category.id
                );
                const isEditing = editingId === category.id;
                return (
                  <tr
                    key={category.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                        {category.code}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <input
                          type="text"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        />
                      ) : (
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {category.name}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {category.fullPath}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {hasChildren ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                          No
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={handleEditSave}
                            className="px-3 py-1 text-xs font-medium text-green-600 hover:text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingId(null);
                              setNewName("");
                              setNewCode("");
                            }}
                            className="px-3 py-1 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleEdit(category.id)}
                            className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                            title="Edit Category"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={async () => {
                              setAddModalParentId(category.id);
                              setNewName("");
                              const nextCode = await generateNextCode();
                              setNewCode(nextCode);
                              setShowAddModal(true);
                            }}
                            className="p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                            title="Add Sub-Category"
                          >
                            <Plus size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(category.id)}
                            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            title="Delete Category"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Modern Pagination */}
        {categories.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing{" "}
              <span className="font-medium text-gray-900 dark:text-white">
                {indexOfFirstItem + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium text-gray-900 dark:text-white">
                {Math.min(indexOfLastItem, totalItems)}
              </span>{" "}
              of{" "}
              <span className="font-medium text-gray-900 dark:text-white">
                {totalItems}
              </span>{" "}
              results
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg border transition-colors ${
                  currentPage === 1
                    ? "border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 cursor-not-allowed"
                    : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                <ChevronLeft size={16} />
              </button>
              {(() => {
                const getVisiblePages = () => {
                  if (totalPages <= 5) {
                    return Array.from({ length: totalPages }, (_, i) => i + 1);
                  }
                  if (currentPage <= 3) {
                    return [1, 2, 3, 4, "...", totalPages];
                  }
                  if (currentPage >= totalPages - 2) {
                    return [
                      1,
                      "...",
                      totalPages - 3,
                      totalPages - 2,
                      totalPages - 1,
                      totalPages,
                    ];
                  }
                  return [
                    1,
                    "...",
                    currentPage - 1,
                    currentPage,
                    currentPage + 1,
                    "...",
                    totalPages,
                  ];
                };

                return getVisiblePages().map((page, idx) =>
                  page === "..." ? (
                    <span
                      key={`ellipsis-${idx}`}
                      className="px-2 text-gray-400 dark:text-gray-500"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => paginate(page)}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                        currentPage === page
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      {page}
                    </button>
                  )
                );
              })()}
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg border transition-colors ${
                  currentPage === totalPages
                    ? "border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 cursor-not-allowed"
                    : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Root Category Modal */}
      {showAddRootModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Add Category
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Electronics"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category Code (auto-generated)
                </label>
                <input
                  type="text"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  placeholder="Auto-generated: C0001, C0002, etc."
                />
                <p className="mt-1 text-xs text-gray-500">
                  Code will be auto-generated if left empty. You can customize
                  it if needed.
                </p>
              </div>
            </div>
            <div className="mt-6 flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddRootModal(false);
                  setNewName("");
                  setNewCode("");
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleAddRoot}>Create Category</Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Delete Category
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the category{" "}
              <span className="font-semibold text-gray-900">
                "{deleteConfirm.name}"
              </span>
              ?
            </p>
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
              ⚠️ This action cannot be undone. The category will be permanently
              deleted.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={cancelDelete}>
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete Category
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Sub-Category Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Add Sub-Category
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Category
                </label>
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                  {categories.find((c) => c.id === addModalParentId)?.name ||
                    "N/A"}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Office Chair"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category Code (auto-generated)
                </label>
                <input
                  type="text"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  placeholder="Auto-generated"
                />
              </div>
            </div>
            <div className="mt-6 flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddModal(false);
                  setAddModalParentId(null);
                  setNewName("");
                  setNewCode("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!newName.trim()) {
                    toast.error("Category name is required");
                    return;
                  }
                  const code = newCode.trim() || (await generateNextCode());
                  try {
                    await apiService.createCategory({
                      name: newName,
                      code: code,
                      parentId: addModalParentId,
                    });
                    toast.success("Sub-category created successfully");
                    setShowAddModal(false);
                    setAddModalParentId(null);
                    setNewName("");
                    setNewCode("");
                    fetchCategories();
                  } catch (err) {
                    toast.error(err.message || "Failed to create sub-category");
                  }
                }}
              >
                Create Sub-Category
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
